import React, { useState, useEffect } from 'react';
import {
  FiPhone,
  FiMail,
  FiGlobe,
  FiMessageCircle,
  FiCalendar,
  FiTrendingUp,
  FiFilter
} from 'react-icons/fi';
import { FaFacebook, FaLine } from 'react-icons/fa';
import AdminLayout from '../../components/admin/layout/AdminLayout';
import { useAdminAuth } from '../../hooks/admin/useAdminAuth';
import { API_URL } from '../../services/api';

// Types
interface ContactInteraction {
  id: number;
  company_id: string;
  company_name?: string;
  interaction_type: 'phone' | 'email' | 'website' | 'facebook' | 'line';
  contact_value?: string;
  ip_address?: string;
  user_agent?: string;
  session_id?: string;
  user_id?: string;
  source_page?: string;
  referrer?: string;
  created_at: string;
}

interface ContactAnalytics {
  total_interactions: number;
  by_type: {
    phone: number;
    email: number;
    website: number;
    facebook: number;
    line: number;
  };
  by_company: Array<{
    company_id: string;
    company_name: string;
    total: number;
    by_type: Record<string, number>;
  }>;
  recent_interactions: ContactInteraction[];
}

const ContactAnalytics: React.FC = () => {
  const { token } = useAdminAuth();
  const [loading, setLoading] = useState(true);
  const [analytics, setAnalytics] = useState<ContactAnalytics | null>(null);
  const [dateRange, setDateRange] = useState('30d');
  const [selectedCompany, setSelectedCompany] = useState<string>('');
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  const breadcrumbs = [
    { label: 'สถิติและรายงาน' },
    { label: 'สถิติการติดต่อบริษัท' }
  ];

  // Fetch analytics data
  useEffect(() => {
    const fetchAnalytics = async () => {
      if (!token) return;

      setLoading(true);
      try {
        

        // Convert dateRange to days
        const daysMap: Record<string, number> = {
          '1d': 1,
          '7d': 7,
          '30d': 30,
          '90d': 90
        };
        const days = daysMap[dateRange] || 30;

        // Use dedicated contact analytics endpoint
        const response = await fetch(
          `${API_URL}/api/tracking/analytics/contacts?days=${days}${selectedCompany ? `&company_id=${selectedCompany}` : ''}`,
          {
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            }
          }
        );

        if (!response.ok) {
          throw new Error('Failed to fetch analytics');
        }

        const result = await response.json();

        if (result.success && result.data) {
          const contactAnalytics: ContactAnalytics = {
            total_interactions: result.data.total_interactions,
            by_type: result.data.by_type,
            by_company: result.data.by_company,
            recent_interactions: result.data.recent_interactions
          };

          setAnalytics(contactAnalytics);
        }
      } catch (error) {
        console.error('Error fetching analytics:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchAnalytics();
  }, [token, dateRange, selectedCompany]);

  // Helper functions
  const getContactIcon = (type: string, className: string = 'h-5 w-5') => {
    switch (type) {
      case 'phone':
        return <FiPhone className={className} />;
      case 'email':
        return <FiMail className={className} />;
      case 'website':
        return <FiGlobe className={className} />;
      case 'facebook':
        return <FaFacebook className={className} />;
      case 'line':
        return <FaLine className={className} />;
      default:
        return <FiPhone className={className} />;
    }
  };

  const getContactColor = (type: string) => {
    switch (type) {
      case 'phone':
        return { bg: 'bg-green-100', text: 'text-green-600', border: 'border-green-200' };
      case 'email':
        return { bg: 'bg-blue-100', text: 'text-blue-600', border: 'border-blue-200' };
      case 'website':
        return { bg: 'bg-indigo-100', text: 'text-indigo-600', border: 'border-indigo-200' };
      case 'facebook':
        return { bg: 'bg-blue-100', text: 'text-blue-700', border: 'border-blue-200' };
      case 'line':
        return { bg: 'bg-green-100', text: 'text-green-600', border: 'border-green-200' };
      default:
        return { bg: 'bg-gray-100', text: 'text-gray-600', border: 'border-gray-200' };
    }
  };

  const getContactLabel = (type: string) => {
    switch (type) {
      case 'phone':
        return 'โทรศัพท์';
      case 'email':
        return 'อีเมล';
      case 'website':
        return 'เว็บไซต์';
      case 'facebook':
        return 'Facebook';
      case 'line':
        return 'Line';
      default:
        return type;
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return new Intl.DateTimeFormat('th-TH', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    }).format(date);
  };

  // Filter companies
  const filteredCompanies = selectedCompany
    ? analytics?.by_company.filter(c => c.company_id === selectedCompany) || []
    : analytics?.by_company || [];

  // Pagination
  const totalPages = Math.ceil(filteredCompanies.length / itemsPerPage);
  const paginatedCompanies = filteredCompanies.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  if (loading) {
    return (
      <AdminLayout title="สถิติการติดต่อบริษัท" breadcrumbs={breadcrumbs}>
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
            <p className="text-gray-600 mt-4">กำลังโหลดข้อมูล...</p>
          </div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout title="สถิติการติดต่อบริษัท" breadcrumbs={breadcrumbs}>
      <div className="space-y-6">
        {/* Header with Filters */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h2 className="text-xl font-semibold text-gray-900">สถิติการติดต่อบริษัท</h2>
            <p className="text-sm text-gray-600 mt-1">
              ติดตามการคลิกติดต่อและ conversion rate ของแต่ละบริษัท
            </p>
          </div>
          <div className="flex items-center gap-3">
            {/* Date Range Filter */}
            <div className="flex items-center gap-2">
              <FiCalendar className="h-4 w-4 text-gray-400" />
              <select
                value={dateRange}
                onChange={(e) => setDateRange(e.target.value)}
                className="block border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 text-sm"
              >
                <option value="1d">วันนี้</option>
                <option value="7d">7 วันที่แล้ว</option>
                <option value="30d">30 วันที่แล้ว</option>
                <option value="90d">90 วันที่แล้ว</option>
              </select>
            </div>
          </div>
        </div>

        {/* Overview Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
          {/* Total Interactions */}
          <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
            <div className="flex items-center justify-between mb-2">
              <FiTrendingUp className="h-8 w-8 text-purple-600" />
            </div>
            <p className="text-2xl font-bold text-gray-900">
              {analytics?.total_interactions?.toLocaleString() || 0}
            </p>
            <p className="text-sm text-gray-600">การติดต่อทั้งหมด</p>
          </div>

          {/* Phone Clicks */}
          <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
            <div className="flex items-center justify-between mb-2">
              <FiPhone className="h-8 w-8 text-green-600" />
            </div>
            <p className="text-2xl font-bold text-gray-900">
              {analytics?.by_type?.phone?.toLocaleString() || 0}
            </p>
            <p className="text-sm text-gray-600">โทรศัพท์</p>
          </div>

          {/* Email Clicks */}
          <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
            <div className="flex items-center justify-between mb-2">
              <FiMail className="h-8 w-8 text-blue-600" />
            </div>
            <p className="text-2xl font-bold text-gray-900">
              {analytics?.by_type?.email?.toLocaleString() || 0}
            </p>
            <p className="text-sm text-gray-600">อีเมล</p>
          </div>

          {/* Website Visits */}
          <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
            <div className="flex items-center justify-between mb-2">
              <FiGlobe className="h-8 w-8 text-indigo-600" />
            </div>
            <p className="text-2xl font-bold text-gray-900">
              {analytics?.by_type?.website?.toLocaleString() || 0}
            </p>
            <p className="text-sm text-gray-600">เว็บไซต์</p>
          </div>

          {/* Social Media */}
          <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
            <div className="flex items-center justify-between mb-2">
              <FiMessageCircle className="h-8 w-8 text-green-600" />
            </div>
            <p className="text-2xl font-bold text-gray-900">
              {((analytics?.by_type?.facebook || 0) + (analytics?.by_type?.line || 0)).toLocaleString()}
            </p>
            <p className="text-sm text-gray-600">Social Media</p>
          </div>
        </div>

        {/* Contact Type Distribution Chart */}
        <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">การกระจายตามประเภทการติดต่อ</h3>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
            {Object.entries(analytics?.by_type || {}).map(([type, count]) => {
              const total = analytics?.total_interactions || 1;
              const percentage = ((count / total) * 100).toFixed(1);
              const colors = getContactColor(type);

              return (
                <div key={type} className="text-center">
                  <div className={`${colors.bg} ${colors.text} rounded-lg p-4 mb-2`}>
                    {getContactIcon(type, 'h-8 w-8 mx-auto')}
                  </div>
                  <p className="text-2xl font-bold text-gray-900">{count}</p>
                  <p className="text-sm text-gray-600">{getContactLabel(type)}</p>
                  <p className="text-xs text-gray-500">{percentage}%</p>
                </div>
              );
            })}
          </div>
        </div>

        {/* Top Companies by Contact Interactions */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200">
          <div className="p-6 border-b border-gray-200">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-semibold text-gray-900">บริษัทที่ได้รับการติดต่อมากที่สุด</h3>
              <div className="flex items-center gap-2">
                <FiFilter className="h-4 w-4 text-gray-400" />
                <select
                  value={selectedCompany}
                  onChange={(e) => {
                    setSelectedCompany(e.target.value);
                    setCurrentPage(1);
                  }}
                  className="block border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 text-sm"
                >
                  <option value="">ทุกบริษัท</option>
                  {analytics?.by_company.map((company) => (
                    <option key={company.company_id} value={company.company_id}>
                      {company.company_name}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    อันดับ
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    ชื่อบริษัท
                  </th>
                  <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                    ทั้งหมด
                  </th>
                  <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                    <FiPhone className="h-4 w-4 inline" />
                  </th>
                  <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                    <FiMail className="h-4 w-4 inline" />
                  </th>
                  <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                    <FiGlobe className="h-4 w-4 inline" />
                  </th>
                  <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                    <FaFacebook className="h-4 w-4 inline" />
                  </th>
                  <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                    <FaLine className="h-4 w-4 inline" />
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {paginatedCompanies.length > 0 ? (
                  paginatedCompanies.map((company, index) => (
                    <tr key={company.company_id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {(currentPage - 1) * itemsPerPage + index + 1}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900">{company.company_name}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-center">
                        <span className="px-2 py-1 inline-flex text-sm leading-5 font-semibold rounded-full bg-purple-100 text-purple-800">
                          {company.total}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-center text-sm text-gray-600">
                        {company.by_type.phone || 0}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-center text-sm text-gray-600">
                        {company.by_type.email || 0}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-center text-sm text-gray-600">
                        {company.by_type.website || 0}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-center text-sm text-gray-600">
                        {company.by_type.facebook || 0}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-center text-sm text-gray-600">
                        {company.by_type.line || 0}
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={8} className="px-6 py-8 text-center text-sm text-gray-500">
                      ไม่พบข้อมูลการติดต่อ
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="px-6 py-4 border-t border-gray-200">
              <div className="flex items-center justify-between">
                <div className="text-sm text-gray-700">
                  แสดง {(currentPage - 1) * itemsPerPage + 1} ถึง{' '}
                  {Math.min(currentPage * itemsPerPage, filteredCompanies.length)} จาก{' '}
                  {filteredCompanies.length} รายการ
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                    disabled={currentPage === 1}
                    className="px-3 py-1 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    ก่อนหน้า
                  </button>
                  <div className="flex items-center gap-1">
                    {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                      <button
                        key={page}
                        onClick={() => setCurrentPage(page)}
                        className={`px-3 py-1 border rounded-md text-sm font-medium ${
                          currentPage === page
                            ? 'bg-blue-600 text-white border-blue-600'
                            : 'border-gray-300 text-gray-700 hover:bg-gray-50'
                        }`}
                      >
                        {page}
                      </button>
                    ))}
                  </div>
                  <button
                    onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                    disabled={currentPage === totalPages}
                    className="px-3 py-1 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    ถัดไป
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Recent Contact Interactions Table */}
        {analytics?.recent_interactions && analytics.recent_interactions.length > 0 && (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200">
            <div className="p-6 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900">การติดต่อล่าสุด</h3>
            </div>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      บริษัท
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      ประเภท
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      ข้อมูลติดต่อ
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      IP Address
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      เวลา
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {analytics.recent_interactions.map((interaction) => {
                    const colors = getContactColor(interaction.interaction_type);
                    return (
                      <tr key={interaction.id} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm font-medium text-gray-900">
                            {interaction.company_name}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`px-2 py-1 inline-flex items-center gap-1 text-xs leading-5 font-semibold rounded-full ${colors.bg} ${colors.text}`}>
                            {getContactIcon(interaction.interaction_type, 'h-3 w-3')}
                            {getContactLabel(interaction.interaction_type)}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                          {interaction.contact_value || '-'}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                          {interaction.ip_address || '-'}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                          {formatDate(interaction.created_at)}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </AdminLayout>
  );
};

export default ContactAnalytics;
